import _ from 'underscore';
import React, { useCallback, useContext, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';

import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import {
  isLegacyLocationAndRadiusEqual,
  BingMapsContextProvider,
  SEARCH_TYPE,
} from '@bingads-webui-react/bing-maps';
import { LocationTargeting, targetOptions } from '@bingads-webui-campaign-react/location-targeting';
import { getLocations } from '@bingads-webui-campaign-react/linkedin-data';

const BING_MAPS_API_KEY = 'AgRuCtepgsuuCYV9J6S6w1j8ay2JJwnu8f5EPgv4kkoJoIuka8yui-NzKrvlESTQ';

export const isLocationDataValid = ({ selectedLocationOption: selectedOption, positiveLocationTargets: targets }) =>
  selectedOption === targetOptions.quick || (selectedOption === targetOptions.specific && targets.length > 0);

export const getLocationTargetsEditorStrings = i18n => ({
  description: i18n.getString(_TL_('For example, coordinates, or state/province.')),
  label: i18n.getString(_TL_('Search for location')),
  choiceGroupLabel: i18n.getString(_TL_('I want to target:')),
});

export const TargetingLocations = ({ defaultLocation, data, onDataChange }) => {
  const mapRef = useRef();

  const i18n = useContext(I18nContext);
  const serverContext = useContext(ServerContext);
  const aid = useMemo(() => serverContext.CurrentAccount.Id, [serverContext]);
  const cid = useMemo(() => serverContext.CurrentCustomer.Id, [serverContext]);

  const strings = useMemo(() => (getLocationTargetsEditorStrings(i18n)), [i18n]);

  const optionConfigs = useMemo(() => ([
    { key: targetOptions.quick, text: _.get(defaultLocation, 'Name') },
    { key: targetOptions.specific, text: i18n.getString(_TL_('Let me choose specific locations')) },
  ]), [i18n, defaultLocation]);

  const { selectedLocationOption, positiveLocationTargets = [] } = data || {};

  const searchOptions = useMemo(() => ({
    getLocations: query => getLocations(cid, aid, query),
  }), [aid, cid]);

  const onSelectedLocationOptionChange = useCallback((newOption) => {
    onDataChange({
      ...data,
      selectedLocationOption: newOption,
    });
  }, [data, onDataChange]);

  const onPositiveLocationTargetsChange = useCallback((newTargets) => {
    onDataChange({
      ...data,
      positiveLocationTargets: newTargets,
    });
  }, [data, onDataChange]);

  const onValidationComplete = useCallback((errorMessage) => {
    onDataChange({
      locationsErrorString: errorMessage,
    });
  }, [onDataChange]);

  return (
    <BingMapsContextProvider
      campaingApiToken={serverContext.Token}
      bingMapsApiKey={BING_MAPS_API_KEY}
      searchOptions={searchOptions}
    >
      <LocationTargeting
        // Config props
        mapRef={mapRef}
        strings={strings}
        optionsConfig={optionConfigs}
        allowNegativeLocationTargeting={false}
        isRadiusExcludeEnabled={false}
        showSearchTypeOptions={false}
        maxLocationTargets={1000}
        customizedLocationEqual={isLegacyLocationAndRadiusEqual}
        defaultSelectedSearchType={SEARCH_TYPE.Boundary}
        useInternalValidation={false}

        // Data props
        selectedLocationOption={selectedLocationOption}
        locationTargets={positiveLocationTargets}

        // Event props
        onChangeLocationOption={onSelectedLocationOptionChange}
        onLocationTargetsChange={onPositiveLocationTargetsChange}
        onNotifyValidationResult={onValidationComplete}
      />
    </BingMapsContextProvider>
  );
};

TargetingLocations.propTypes = {
  defaultLocation: PropTypes.shape({
    Name: PropTypes.string,
  }).isRequired,
  data: PropTypes.shape({}).isRequired,
  onDataChange: PropTypes.func.isRequired,
};
